import { MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useBntAsistenteOptional } from "@/modules/asistente/BntAsistenteContext";
import {
  getAssistantPopupRef,
  openBntAsistente,
  shouldPreferIntegratedAssistantPanel,
} from "@/modules/asistente/bntAsistenteRoutes";
import { readAssistantPopupActive } from "@/modules/asistente/bntAsistenteMessaging";

/**
 * Acceso rápido al BNT Asistente — panel integrado en Cursor; ventana flotante en navegador externo.
 */
export default function BntAsistenteLauncher() {
  const ctx = useBntAsistenteOptional();
  const badgeTotal = ctx?.badgeTotal || 0;
  const panelState = ctx?.panelState || "closed";
  const unreadTabs = ctx?.unreadTabs || {};
  const hasUnread = badgeTotal > 0 || Object.keys(unreadTabs).length > 0;
  const preferIntegrated = shouldPreferIntegratedAssistantPanel();
  const [popupOpen, setPopupOpen] = useState(() => readAssistantPopupActive());

  useEffect(() => {
    const syncPopup = () => {
      setPopupOpen(readAssistantPopupActive() || Boolean(getAssistantPopupRef()));
    };
    syncPopup();
    const id = window.setInterval(syncPopup, 2500);
    window.addEventListener("focus", syncPopup);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", syncPopup);
    };
  }, []);

  const handleClick = (event) => {
    if (!preferIntegrated && event.shiftKey && ctx) {
      ctx.togglePanel();
      return;
    }
    const mode = openBntAsistente({ tab: ctx?.activeTab });
    if (mode === "popup") setPopupOpen(true);
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    if (ctx) {
      ctx.togglePanel();
    }
  };

  const isOpen = panelState === "open";
  const isMinimized = panelState === "minimized";

  return (
    <div
      className={`fixed z-[45] flex flex-col items-end gap-1 ${
        isMinimized ? "bottom-16 right-5" : "bottom-5 right-5"
      }`}
      data-testid="bnt-asistente-launcher"
    >
      <Button
        type="button"
        size="lg"
        className={`rounded-full h-12 w-12 shadow-lg p-0 relative ${
          isOpen || popupOpen
            ? "bg-[#0a2248] ring-2 ring-[#0F2D5C]/30"
            : hasUnread
              ? "bg-[#0F2D5C] hover:bg-[#0a2248] ring-2 ring-rose-500 animate-pulse"
              : "bg-[#0F2D5C] hover:bg-[#0a2248]"
        }`}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        title={
          isOpen
            ? "Minimizar panel integrado"
            : preferIntegrated
              ? "Abrir BNT Asistente (panel integrado)"
              : "Abrir ventana flotante BNT Asistente (Shift+clic: panel integrado · clic derecho: panel)"
        }
        aria-label={preferIntegrated ? "Abrir BNT Asistente" : "Abrir ventana flotante BNT Asistente"}
        aria-pressed={isOpen || popupOpen}
        data-testid="bnt-asistente-launcher-btn"
      >
        <MessageCircle className="h-5 w-5" aria-hidden />
        {badgeTotal > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">
            {badgeTotal > 99 ? "99+" : badgeTotal}
          </span>
        ) : null}
      </Button>
      <span className="text-[10px] text-slate-500 bg-white/90 px-2 py-0.5 rounded-sm border border-slate-200 shadow-sm hidden sm:inline">
        {popupOpen ? "Ventana flotante" : isMinimized ? "En barra de tareas" : isOpen ? "Panel integrado" : "BNT Asistente"}
      </span>
    </div>
  );
}
